import { useState } from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import IstresMoiTitle from "@/components/IstresMoiTitle";
import { ISTRES_LOGO } from "@/lib/istres";
import {
  LayoutDashboard, MessageSquare, ClipboardList, Gamepad2, Ticket,
  LogOut, Users, ShieldCheck, Store, Calendar, Menu, X, FileCheck, Sparkles, Gift,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const ROLE = {
  admin: { label: "Mairie", emoji: "🏛️", bg: "bg-plum-100", fg: "text-plum-700" },
  commercant: { label: "Commerçant", emoji: "🛍️", bg: "bg-sun-100", fg: "text-sun-700" },
  habitant: { label: "Habitant", emoji: "🙌", bg: "bg-pink-100", fg: "text-pink-600" },
};

const navByRole = {
  admin: [
    { to: "/admin", label: "Accueil", icon: LayoutDashboard, end: true },
    { to: "/admin/remontees", label: "Remontées", icon: MessageSquare },
    { to: "/admin/sondages", label: "Sondages", icon: ClipboardList },
    { to: "/admin/jeux", label: "Quiz", icon: Gamepad2 },
    { to: "/admin/utilisateurs", label: "Utilisateurs", icon: Users },
    { to: "/admin/residences", label: "Justificatifs", icon: FileCheck },
    { to: "/admin/citoyens", label: "Vérif. CNI", icon: Sparkles },
    { to: "/admin/avantages", label: "Avantages", icon: Gift },
    { to: "/admin/pseudos", label: "Modération", icon: ShieldCheck },
  ],
  commercant: [
    { to: "/commercant", label: "Mon espace", icon: LayoutDashboard, end: true },
    { to: "/commercant/fiche", label: "Ma fiche", icon: Store },
    { to: "/commercant/evenements", label: "Événements", icon: Calendar },
    { to: "/remontees", label: "Remontées", icon: MessageSquare },
    { to: "/sondages", label: "Sondages", icon: ClipboardList },
  ],
  habitant: [
    { to: "/habitant", label: "Accueil", icon: LayoutDashboard, end: true },
    { to: "/remontees", label: "Remontées", icon: MessageSquare },
    { to: "/sondages", label: "Sondages", icon: ClipboardList },
    { to: "/jeux", label: "Jeux", icon: Gamepad2 },
    { to: "/bons", label: "Mes bons", icon: Ticket },
    { to: "/mes-avantages", label: "Avantages", icon: Sparkles },
  ],
};

function SidebarContent({ nav, role, user, onLogout, onNavigate }) {
  return (
    <div className="flex flex-col h-full" data-testid={`${user.role}-nav`}>
      <div className="flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-0 min-w-0 flex-1" aria-label="Istres & Moi · Accueil">
          <img src={ISTRES_LOGO} alt="Logo Ville d'Istres" className="h-10 w-auto object-contain shrink-0" />
          <div className="leading-none min-w-0 -ml-2">
            <IstresMoiTitle className="text-plum-700 text-[17px] whitespace-nowrap" />
            <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-plum-700/55 mt-1 whitespace-nowrap">Ville d&apos;Istres</div>
          </div>
        </Link>
        <NotificationBell />
      </div>

      <div className={`mt-6 rounded-2xl p-3.5 ${role.bg} flex items-center gap-2.5`}>
        <span className="text-xl">{role.emoji}</span>
        <div className="min-w-0">
          <div className={`text-[10px] font-bold uppercase tracking-wider ${role.fg}`}>
            Vous êtes
          </div>
          <div className={`font-display font-bold text-sm ${role.fg} truncate`}>
            {user.pseudo || user.name}
          </div>
        </div>
      </div>

      <div className="mt-7 flex-1 overflow-y-auto">
        <div className="phenix-eyebrow mb-3 px-2">Navigation</div>
        <nav className="flex flex-col gap-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={`nav-${item.to.replace(/[/]/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-3 rounded-2xl text-[15px] font-semibold transition-all ${
                  isActive
                    ? "bg-plum-600 text-white shadow-[0_4px_16px_-4px_rgba(91,33,182,0.4)]"
                    : "text-plum-700/70 hover:bg-plum-50 hover:text-plum-700"
                }`
              }
            >
              <item.icon size={18} strokeWidth={2.2} />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <button
        onClick={onLogout}
        data-testid="logout-btn"
        className="btn-pill bg-cream-50 hover:bg-pink-50 hover:text-pink-600 text-plum-700/60 py-2.5 px-4 mt-4"
      >
        <LogOut size={14} strokeWidth={2.2} />
        Déconnexion
      </button>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = navByRole[user.role] || [];
  const role = ROLE[user.role];

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-cream-50 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-[300px] bg-white border-r border-cream-200 px-5 py-6 sticky top-0 h-screen">
        <SidebarContent nav={nav} role={role} user={user} onLogout={onLogout} />
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile header — city logo left, "Istres & Moi" centered, menu right */}
        <header className="lg:hidden relative flex items-center justify-between px-3 py-2.5 bg-white border-b border-cream-200 sticky top-0 z-30 gap-2">
          <Link to="/" aria-label="Accueil" className="shrink-0 z-10">
            <img src={ISTRES_LOGO} alt="Logo Ville d'Istres" className="h-14 w-auto object-contain" data-testid="appshell-city-logo" />
          </Link>

          <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 flex flex-col items-center leading-none pointer-events-none">
            <IstresMoiTitle className="text-plum-700 text-[20px] whitespace-nowrap" />
            <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-plum-700/55 mt-1">Ville d&apos;Istres</span>
          </div>

          <div className="flex items-center gap-2 z-10">
            <NotificationBell />
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-toggle"
                  aria-label="Ouvrir le menu"
                  className="w-11 h-11 rounded-full bg-plum-700 hover:bg-plum-800 text-white grid place-items-center shadow-sm"
                >
                  <Menu size={20} strokeWidth={2.4} />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] sm:w-[340px] p-6 bg-white">
                <SidebarContent
                  nav={nav}
                  role={role}
                  user={user}
                  onLogout={onLogout}
                  onNavigate={() => setOpen(false)}
                />
              </SheetContent>
            </Sheet>
          </div>
        </header>

        <div className="px-4 sm:px-6 lg:px-12 py-5 sm:py-7 lg:py-10 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
